import { AxiosError } from "axios";
import { createApi } from "../../../../utils/createApi";

// Bulk alert settings (ENG-2485): apply one alert-delivery change across many
// sites in a single admin request. POST alert-settings/bulk/ with
// dry_run=true previews, dry_run=false applies.

/** Integrations the bulk endpoint accepts. Patriot first; add more here. */
export type BulkIntegration = "patriot";

export interface PatriotBulkChanges {
  patriot_send_video: boolean;
  draw_ignore_zones: boolean;
}

export interface PatriotBulkFilter {
  patriot_client_no?: string;
  patriot_server?: string;
}

export interface BulkAlertSettingsRequest {
  site_ids: number[];
  integration: BulkIntegration;
  changes: PatriotBulkChanges;
  filter?: PatriotBulkFilter;
  dry_run: boolean;
}

export interface BulkAlertRow {
  alert_id: number;
  patriot_client_no?: string | null;
  patriot_zone_number?: string | number | null;
  before: PatriotBulkChanges;
  after: PatriotBulkChanges;
  changed: boolean;
}

export interface BulkCameraResult {
  camera_id: number;
  camera_name: string;
  alerts: BulkAlertRow[];
}

export interface BulkSiteResult {
  site_id: number;
  site_name: string;
  cameras: BulkCameraResult[];
  changed_count: number;
}

export type BulkSkipReason = "no_access" | "not_found" | "no_matching_alerts";

export interface BulkSkippedSite {
  site_id: number;
  reason: BulkSkipReason | string;
}

export interface BulkAlertSettingsResponse {
  dry_run: boolean;
  integration: BulkIntegration;
  changed_count: number;
  sites: BulkSiteResult[];
  skipped_sites: BulkSkippedSite[];
}

export const SKIP_REASON_TEXT: Record<BulkSkipReason, string> = {
  no_access: "You don't have permission to edit this site",
  not_found: "Site not found",
  no_matching_alerts: "No Patriot alerts match",
};

export const skipReasonText = (reason: string): string =>
  SKIP_REASON_TEXT[reason as BulkSkipReason] ?? "Skipped";

const ERROR_CODE_TEXT: Record<string, string> = {
  bad_integration: "That integration can't be changed in bulk yet.",
  no_sites: "Select at least one site.",
  too_many_sites:
    "Too many sites for one change. Select fewer sites and try again.",
  bad_changes: "That change isn't valid. Pick what Patriot receives again.",
};

/**
 * Plain-language message for a failed request, or null when the caller should
 * hand the error to universalAPIErrorHandler (403s included, so an expired
 * token still refreshes and retries; network, 5xx).
 */
export const NO_PERMISSION_TEXT =
  "You don't have permission to change alert settings for these sites.";

export const bulkErrorMessage = (error: AxiosError): string | null => {
  if (error.response?.status === 400) {
    const data = (error.response?.data ?? {}) as Record<string, unknown>;
    const code = data.code ?? data.error ?? data.detail;
    return (
      (typeof code === "string" && ERROR_CODE_TEXT[code]) ||
      "The change couldn't be made. Check your choices and try again."
    );
  }
  return null;
};

export const postBulkAlertSettings = async (
  request: BulkAlertSettingsRequest
): Promise<BulkAlertSettingsResponse> => {
  const response = await createApi("").post("alert-settings/bulk/", request);
  return response.data;
};

/** Drop blank filter fields; undefined when nothing is left. */
export const cleanFilter = (
  filter: PatriotBulkFilter
): PatriotBulkFilter | undefined => {
  const cleaned: PatriotBulkFilter = {};
  const clientNo = filter.patriot_client_no?.trim();
  const server = filter.patriot_server?.trim();
  if (clientNo) cleaned.patriot_client_no = clientNo;
  if (server) cleaned.patriot_server = server;
  return Object.keys(cleaned).length ? cleaned : undefined;
};

/** Alert counts across a response: changed vs already set this way. */
export const countAlerts = (
  sites: BulkSiteResult[]
): { changed: number; unchanged: number } => {
  let changed = 0;
  let unchanged = 0;
  sites.forEach((site) =>
    site.cameras.forEach((camera) =>
      camera.alerts.forEach((alert) => {
        if (alert.changed) changed += 1;
        else unchanged += 1;
      })
    )
  );
  return { changed, unchanged };
};
